import { Autocomplete, Chip, TextField } from '@mui/material';
import { Control, Controller } from 'react-hook-form';

import { cn } from '@/lib/utils';
import { Option } from '@/types/Filter';

type Props = {
	title: string;
	options: Option[];
	control: Control<any>;
	name: string;
	setSearch: (val: string) => void;
	width?: string;
	required?: boolean;
	disabled?: boolean;
	multiple?: boolean;
	className?: string;
	containerClassName?: string;
};

const SearchSelect = ({
	title,
	options,
	control,
	name,
	setSearch,
	width,
	required,
	disabled,
	multiple = false,
	className,
	containerClassName,
}: Props) => {
	const chipStyles = {
		maxWidth: 100,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	};

	const getTextFieldStyles = (multiple: boolean) => ({
		minHeight: '2rem',
		'& input': {
			padding: multiple ? '0' : '0.5rem 0',
			fontSize: '12px',
			width: multiple ? '0px' : 'auto',
			height: multiple ? '0px' : 'auto',
			minWidth: multiple ? '0px' : 'auto',
		},
		'& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
			minHeight: '2rem',
			fontSize: '12px',
			...(multiple && {
				'& .MuiAutocomplete-input:not(:focus-within)': {
					padding: '0',
				},
			}),
		},
		...(multiple && {
			'& input:focus-within': {
				padding: '0.5rem 0',
				width: 'auto',
				height: 'auto',
				minWidth: 'auto',
			},
			'& .MuiOutlinedInput-root.MuiInputBase-sizeSmall:focus-within': {
				minHeight: '2rem',
			},
		}),
	});

	const getAutocompleteStyles = (multiple: boolean) => ({
		width,
		fontSize: '12px',
		'& .MuiAutocomplete-paper': {
			height: 'auto',
		},
		'& .MuiAutocomplete-inputRoot': {
			height: multiple ? 'auto' : '2rem',
			minHeight: '2rem',
		},
		'& .MuiAutocomplete-clearIndicator': {
			height: '1.5rem',
		},
		'& + .MuiAutocomplete-popper .MuiAutocomplete-option': {
			fontSize: '12px',
		},
		'& .MuiAutocomplete-tagSizeSmall': {
			maxWidth: 'calc(100% - 35px)',
			height: '100%',
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},
	});

	return (
		<div className={containerClassName}>
			<div className={'text-[12px] text-[#4A4A4A]'}>
				{title}
				{required && <span className="text-red">*</span>}
			</div>
			<Controller
				name={name}
				control={control}
				render={({ field: { onChange, value, ref } }) => {
					const getValue = () => {
						if (multiple) {
							if (!value || value.length === 0) return [];
							return options.filter((option) => value.includes(option.value));
						} else {
							return options.find((option) => option.value === value) || null;
						}
					};

					return (
						<Autocomplete
							disablePortal
							multiple={multiple}
							size="small"
							limitTags={1}
							options={options}
							value={getValue()}
							onChange={(event, newValue) => {
								if (multiple) {
									if (Array.isArray(newValue)) {
										onChange(newValue.map((option) => option.value));
									} else {
										onChange([]);
									}
								} else {
									onChange((newValue as Option)?.value || '');
								}
							}}
							disabled={disabled}
							renderTags={(value, getTagProps) =>
								value.map((option, index) => (
									<Chip
										label={option.label}
										{...getTagProps({ index })}
										size="small"
										sx={chipStyles}
									/>
								))
							}
							renderInput={(params) => (
								<TextField
									{...params}
									required={required}
									inputRef={ref}
									onChange={(e) => setSearch(e.target.value)}
									sx={getTextFieldStyles(multiple)}
								/>
							)}
							noOptionsText="Nenhum resultado encontrado"
							className={cn('w-[13.5rem]', className)}
							sx={getAutocompleteStyles(multiple)}
						/>
					);
				}}
			/>
		</div>
	);
};

export default SearchSelect;
